import {
    Float,
    PerspectiveCamera,
    useScroll,
    useTexture,
} from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { gsap } from "gsap";
import {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from "react";
import * as THREE from "three";
import { Group, Vector3 } from "three";
import { fadeOnBeforeCompile } from "../utils/fadeMaterial";
import Background from "./Background";
import Speed from "./Speed";
import TextSection from "./TextSection";
import { usePlay } from "../contexts/Play";

const LINE_NB_POINTS = 1000;
const CURVE_DISTANCE = 50;
const CURVE_AHEAD_CAMERA = 0.008;
const CURVE_AHEAD_AIRPLANE = 0.02;
const AIRPLANE_MAX_ANGLE = 35;
const FRICTION_DISTANCE = 100;

export const Experience1 = () => {


    const curvePoints = useMemo(() => [
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(0, 0, -CURVE_DISTANCE),
        new THREE.Vector3(35, 0, -2 * CURVE_DISTANCE),
        new THREE.Vector3(35, 0, -3 * CURVE_DISTANCE),
        new THREE.Vector3(35, 0, -4 * CURVE_DISTANCE),
        new THREE.Vector3(0, 0, -5 * CURVE_DISTANCE),
        new THREE.Vector3(0, 0, -6 * CURVE_DISTANCE),
        new THREE.Vector3(0, 10, -7 * CURVE_DISTANCE),
    ], []);

    const curve = useMemo(() => {
        return new THREE.CatmullRomCurve3(curvePoints, false, "catmullrom", 0.5);
    }, []);

// Sample points along curve for the dashed line
const linePoints = useMemo(() => curve.getPoints(500), [curve]);

    const shape = useMemo(() => {
        const s = new THREE.Shape();
        const halfWidth = 0;
        s.moveTo(-halfWidth, 0);
        s.lineTo(halfWidth, 0);
        s.lineTo(halfWidth, 0.1);
        s.lineTo(-halfWidth, 0.1);
        s.lineTo(-halfWidth, 2);
        return s;
    }, []);

    const dashedLinePoints = useMemo(() => {
        return curve.getPoints(LINE_NB_POINTS);
    }, []);


    const dashedLineGeometry = useMemo(() => {
        const points = curve.getPoints(LINE_NB_POINTS);
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(geometry, new THREE.LineDashedMaterial({
            color: "white",
            dashSize: 5,
            gapSize: 5,
            linewidth: 1,
        }));
        geometry.computeBoundingSphere();
        line.computeLineDistances();
        return geometry;
    }, [curve]);

    const roadTexture = useTexture(
        "/Images/road_texture.jpg"
    );
    roadTexture.wrapS = roadTexture.wrapT = THREE.RepeatWrapping;
    roadTexture.repeat.set(1, 1); // Adjust based on length of the road

    const sceneOpacity = useRef(0);

    const textSections = useMemo(() => [
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.012).clone().add(new Vector3(-0.8, -1.5, 0)),
            rotation: new THREE.Euler(0.5, -0.2, 0.2), // Make sure it's straight
            scrolltext: `Scroll Here`,
        },
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.02).clone().add(new Vector3(-3, 1.2, 0)),
            rotation: new THREE.Euler(0, 0.2, 0), // Make sure it's straight
            subtitle: `Mr. Mohamed Iesha started off as a street trader in Penang.`,
        },
        // {
        //     cameraRailDist: -1,
        //     position: curve.getPointAt(0.02).clone().add(new Vector3(0, 1, 0)),
        //     rotation: new THREE.Euler(1, 0.2, 0), // Make sure it's straight
        //     subtitle: `Mr. Mohamed Iesha started off as a street trader in Penang.`,
        // },
        {
            cameraRailDist: 3,
            position: curve.getPointAt(0.15).clone().add(new Vector3(-3.5, 1, 0)),
            rotation: new THREE.Euler(0, -0.5, 0), // Make sure it's straight
            subtitle: `Mr. Mohamed Nasar registered AL-BIDAYAH as sole proprietor and registered Sdn Bhd`,
        },
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.22).clone().add(new Vector3(-5, 1, 0)),
            subtitle: `Pioneered 24/7 dining in Selayang.`,
            rotation: new THREE.Euler(0, -0.5, 0), // Make sure it's straight
        },
        {
            cameraRailDist: 1.5,
            position: curve.getPointAt(0.32).clone().add(new Vector3(-3.5, 2.3, 0)),
            rotation: new THREE.Euler(0, -0.5, 0), // Make sure it's straight
            // title: "Services",
            subtitle: `Mr. Burhan has been appointed as the 3rd successor, and the AL-BIDAYAH trademark has also been registered.`,
        },
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.37).clone().add(new Vector3(-2.5, 1, 0)),
            rotation: new THREE.Euler(0, 0.1, 0), // Make sure it's straight
            // title: "Fear of flying?",
            subtitle: `Grew to 20+ outlets under strong leadership.`,
        },
        {
            cameraRailDist: 1.5,
            position: curve.getPointAt(0.45).clone().add(new Vector3(-3, 0.8, 0)),
            rotation: new THREE.Euler(0, 0.2, 0), // Make sure it's straight
            // title: "Movies",
            subtitle: `Future expansion is in motion.`,
        },
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.53).clone().add(new Vector3(-3.5, 1.5, 0)),
            rotation: new THREE.Euler(0, 0.2, 0), // Make sure it's straight
            // title: "Meal Options",
            subtitle: `Expanded into Singapore, Dubai, and Saudi Arabia.`,
        },
        {
            cameraRailDist: 1.5,
            position: curve.getPointAt(0.62).clone().add(new Vector3(-5.5, 1, 0)),
            rotation: new THREE.Euler(0, 1, 0), // Make sure it's straight
            // title: "Kids' Zone",
            // subtitle: `Director Ahmad introduced digital and customer systems.`,
            subtitle: `Mr. Anwar introduced the new era of digital marketing.`,
        },
        {
            cameraRailDist: -1,
            position: curve.getPointAt(0.73).clone().add(new Vector3(-4.5, 1, 0)),
            rotation: new THREE.Euler(0, 1, 0), // Make sure it's straight
            // title: "Flight Info",
            subtitle: `Brand and dining experience fully revamped.`,
        },
        {
            cameraRailDist: 1.5,
            position: curve.getPointAt(0.99).clone().add(new Vector3(-1.5, 4, 0)),
            title: "Stay Tuned...",
            subtitle: `More branches launching soon!`,
        },
    ], [curve]);


    const ImagePlane = ({ image, position, scale = [5, 3], rotation = [0, 0, 0] }) => {
        const texture = useTexture(image);
        return (
            <mesh
                position={position}
                // rotation={new THREE.Euler(...rotation)}
                rotation={new THREE.Euler(rotation[0], rotation[1], rotation[2])}
            // renderOrder={1}
            >
                <planeGeometry args={scale} />
                <meshBasicMaterial
                    map={texture}
                    transparent
                    // alphaTest={0.5}
                    depthWrite={false}
                />
            </mesh>
        );
    };


    const customImages = useMemo(() => [
        {
            image: "/models/Road_Map_Images/Untitled_Artwork.png",
            position: curve.getPointAt(0.03).clone().add(new Vector3(3, 1, 0)),
            scale: [4, 6], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.03).clone().add(new Vector3(-1, 0.5, 0)),
            rotation: [0, 0.7, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.03).clone().add(new Vector3(-1, 0.5, 0)),
            rotation: [0, 0.7, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_1.png",
            position: curve.getPointAt(0.14).clone().add(new Vector3(3, 0.5, 0)),
            rotation: [0, -0.5, 0],  // Rotate 30 degrees along the Y-axis
            scale: [2.5, 3], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.15).clone().add(new Vector3(-2, 0.1, 0)),
            rotation: [0, -1, 0],  // Rotate 30 degrees along the Y-axis
            scale: [7.5, 3.5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.15).clone().add(new Vector3(-2, 0.1, 0)),
            rotation: [0, -1, 0],  // Rotate 30 degrees along the Y-axis
            scale: [7.5, 3.5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_3.png",
            position: curve.getPointAt(0.21).clone().add(new Vector3(4.5, 1, 0)),
            rotation: [0, -0.5, 0],  // Rotate 30 degrees along the Y-axis
            scale: [5, 5],
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.22).clone().add(new Vector3(-4, 0.5, 0)),
            rotation: [0, -1, 0],  // Rotate 30 degrees along the Y-axis
            scale: [8, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.22).clone().add(new Vector3(-4, 0.5, 0)),
            rotation: [0, -1, 0],  // Rotate 30 degrees along the Y-axis
            scale: [8, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_4.png",
            position: curve.getPointAt(0.30).clone().add(new Vector3(4, 1, 0)),
            rotation: [0, -0.6, 0],  // Rotate 30 degrees along the Y-axis
            scale: [5, 6],
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.32).clone().add(new Vector3(-2, 1, 0)),
            rotation: [0, -0.5, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.32).clone().add(new Vector3(-2, 1, 0)),
            rotation: [0, -0.5, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Outlets.png",
            position: curve.getPointAt(0.37).clone().add(new Vector3(3, 0, 0)),
            rotation: [0, 0, 0],  // Rotate 30 degrees along the Y-axis
            scale: [4, 4.5],
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.375).clone().add(new Vector3(-0.5, 0.5, 0)),
            rotation: [0, 0, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.375).clone().add(new Vector3(-0.5, 0.5, 0)),
            rotation: [0, 0, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_6.png",
            position: curve.getPointAt(0.45).clone().add(new Vector3(3, 1, 0)),
            rotation: [0, 0.1, 0],  // Rotate 30 degrees along the Y-axis
            scale: [4, 5],
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.458).clone().add(new Vector3(-2, 0.5, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.458).clone().add(new Vector3(-2, 0.5, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_7.png",
            position: curve.getPointAt(0.53).clone().add(new Vector3(3.5, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [5, 5],
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.54).clone().add(new Vector3(-1.7, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/Images/cloud.png",
            position: curve.getPointAt(0.54).clone().add(new Vector3(-1.7, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_8.png",
            position: curve.getPointAt(0.64).clone().add(new Vector3(4, 1, 0)),
            rotation: [0, 0.5, 0],  // Rotate 30 degrees along the Y-axis
            scale: [5, 6],
        },
        {
          image: "/Images/cloud.png",
          position: curve.getPointAt(0.63).clone().add(new Vector3(-3, 0.5, 0)),
          rotation: [0, 1, 0],  // Rotate 30 degrees along the Y-axis
          scale: [9, 5], // Adjust as needed
        },
        {
          image: "/Images/cloud.png",
          position: curve.getPointAt(0.63).clone().add(new Vector3(-3, 0.5, 0)),
          rotation: [0, 1, 0],  // Rotate 30 degrees along the Y-axis
          scale: [9, 5], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/Road_Map_9.png",
            position: curve.getPointAt(0.75).clone().add(new Vector3(3, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [5, 5],
        },
        {
          image: "/Images/cloud.png",
          position: curve.getPointAt(0.74).clone().add(new Vector3(-4, 0.5, 0)),
          rotation: [0, 0.5, 0],  // Rotate 30 degrees along the Y-axis
          scale: [7, 4], // Adjust as needed
        },
        {
          image: "/Images/cloud.png",
          position: curve.getPointAt(0.74).clone().add(new Vector3(-4, 0.5, 0)),
          rotation: [0, 0.5, 0],  // Rotate 30 degrees along the Y-axis
          scale: [7, 4], // Adjust as needed
        },
        {
            image: "/models/Road_Map_Images/soon.png",
            position: curve.getPointAt(0.99).clone().add(new Vector3(0.1, 0.05, 0)),
            scale: [8, 8],
        },
        {
            image: "/Images/cloud_new.png",
            position: curve.getPointAt(1).clone().add(new Vector3(1, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [27, 15], // Adjust as needed
        },
        {
            image: "/Images/cloud_new.png",
            position: curve.getPointAt(1).clone().add(new Vector3(-6, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [27, 15], // Adjust as needed
        },
        {
            image: "/Images/cloud_new.png",
            position: curve.getPointAt(1).clone().add(new Vector3(8, 1, 0)),
            rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
            scale: [27, 15], // Adjust as needed
        },
        // {
        //     image: "/Images/cloud_new.png",
        //     position: curve.getPointAt(1).clone().add(new Vector3(1, 1, 0)),
        //     rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
        //     scale: [27, 15], // Adjust as needed
        // },
        // {
        //     image: "/Images/cloud_new.png",
        //     position: curve.getPointAt(1).clone().add(new Vector3(1, 1, 0)),
        //     rotation: [0, 0.2, 0],  // Rotate 30 degrees along the Y-axis
        //     scale: [27, 15], // Adjust as needed
        // },
    ], [curve]);



    const cameraGroup = useRef();
    const cameraRail = useRef();
    const camera = useRef();
    const scroll = useScroll();
    const lastScroll = useRef(0);
    const { play, setHasScroll, end, setEnd } = usePlay();
    const airplane = useRef();
    const tl = useRef();
    const planeInTl = useRef();
    const planeOutTl = useRef();

    useFrame((_state, delta) => {
        if (window.innerWidth > window.innerHeight) {
            // LANDSCAPE
            camera.current.fov = 30;
            camera.current.position.z = 5;
        } else {
            // PORTRAIT
            camera.current.fov = 80;
            camera.current.position.z = 2;
        }

        if (lastScroll.current <= 0 && scroll.offset > 0) {
            setHasScroll(true);
        }

        if (play && !end && sceneOpacity.current < 1) {
            sceneOpacity.current = THREE.MathUtils.lerp(
                sceneOpacity.current,
                1,
                delta * 0.1
            );
        }

        if (end && sceneOpacity.current > 0) {
            sceneOpacity.current = THREE.MathUtils.lerp(
                sceneOpacity.current,
                0,
                delta
            );
        }

        // lineMaterialRef.current.opacity = sceneOpacity.current;

        if (end) {
            // You can keep this if you want to stop updates when 'end' is true,
            // Or remove if you want to allow full scroll behavior.
            return;
        }

        const scrollOffset = Math.max(0, scroll.offset);

        let friction = 1;
        let resetCameraRail = true;
        // LOOK TO CLOSE TEXT SECTIONS
        textSections.forEach((textSection) => {
            const distance = textSection.position.distanceTo(
                cameraGroup.current.position
            );

            if (distance < FRICTION_DISTANCE) {
                friction = Math.max(distance / FRICTION_DISTANCE, 2);
                const targetCameraRailPosition = new Vector3(
                    (1 - distance / FRICTION_DISTANCE) * textSection.cameraRailDist,
                    0,
                    0
                );
                cameraRail.current.position.lerp(targetCameraRailPosition, delta);
                resetCameraRail = false;
            }
        });
        if (resetCameraRail) {
            const targetCameraRailPosition = new Vector3(0, 0, 0);
            cameraRail.current.position.lerp(targetCameraRailPosition, delta);
        }

        // CALCULATE LERPED SCROLL OFFSET
        let lerpedScrollOffset = THREE.MathUtils.lerp(
            lastScroll.current,
            scrollOffset,
            delta * friction
        );
        // PROTECT BELOW 0 AND ABOVE 1
        lerpedScrollOffset = Math.min(lerpedScrollOffset, 0.95);
        // lerpedScrollOffset = Math.min(lerpedScrollOffset, 1);
        lerpedScrollOffset = Math.max(lerpedScrollOffset, 0);

        lastScroll.current = lerpedScrollOffset;
        tl.current.seek(lerpedScrollOffset * tl.current.duration());

        // HANDLE SCROLL OFFSET = 1: clamp plane at end of curve
        if (lerpedScrollOffset >= 1) {
            // Clamp cameraGroup to last point on curve
            const lastPoint = curvePoints[curvePoints.length - 1];
            cameraGroup.current.position.lerp(lastPoint, delta * 24);

            // Make the group look ahead on the curve (point stays the same at end)
            cameraGroup.current.lookAt(
                lastPoint.clone().add(new THREE.Vector3(0, 0, -1))
            );

            // Airplane rotation: set to default or facing forward (optional)
            // Here we just keep last rotation stable
        } else {
            // Normal animation updates

            const curPoint = curve.getPoint(lerpedScrollOffset);

            // Follow the curve points
            cameraGroup.current.position.lerp(curPoint, delta * 24);

            // Make the group look ahead on the curve
            const lookAtPoint = curve.getPoint(
                Math.min(lerpedScrollOffset + CURVE_AHEAD_CAMERA, 1)
            );

            const currentLookAt = cameraGroup.current.getWorldDirection(
                new THREE.Vector3()
            );
            const targetLookAt = new THREE.Vector3()
                .subVectors(curPoint, lookAtPoint)
                .normalize();

            const lookAt = currentLookAt.lerp(targetLookAt, delta * 24);
            cameraGroup.current.lookAt(
                cameraGroup.current.position.clone().add(lookAt)
            );

            // Airplane rotation

            const tangent = curve.getTangent(lerpedScrollOffset + CURVE_AHEAD_AIRPLANE);

            const nonLerpLookAt = new Group();
            nonLerpLookAt.position.copy(curPoint);
            nonLerpLookAt.lookAt(nonLerpLookAt.position.clone().add(targetLookAt));

            tangent.applyAxisAngle(
                new THREE.Vector3(0, 1, 0),
                -nonLerpLookAt.rotation.y
            );

            let angle = Math.atan2(-tangent.z, tangent.x);
            angle = -Math.PI / 2 + angle;

            let angleDegrees = (angle * 180) / Math.PI;
            angleDegrees *= 2.4; // stronger angle

            // LIMIT PLANE ANGLE
            if (angleDegrees < 0) {
                angleDegrees = Math.max(angleDegrees, -AIRPLANE_MAX_ANGLE);
            }
            if (angleDegrees > 0) {
                angleDegrees = Math.min(angleDegrees, AIRPLANE_MAX_ANGLE);
            }

            // SET BACK ANGLE
            angle = (angleDegrees * Math.PI) / 180;

            const targetAirplaneQuaternion = new THREE.Quaternion().setFromEuler(
                new THREE.Euler(
                    airplane.current.rotation.x,
                    airplane.current.rotation.y,
                    angle
                )
            );
            airplane.current.quaternion.slerp(targetAirplaneQuaternion, delta * 2);
        }
    });

    const backgroundColors = useRef({
        colorA: "#3535cc",
        colorB: "#abaadd",
    });

    useLayoutEffect(() => {
        tl.current = gsap.timeline();

        tl.current.to(backgroundColors.current, {
            duration: 1,
            colorA: "#6f35cc",
            colorB: "#ffad30",
        });
        tl.current.to(backgroundColors.current, {
            duration: 1,
            colorA: "#424242",
            colorB: "#ffcc00",
        });
        tl.current.to(backgroundColors.current, {
            duration: 1,
            colorA: "#81318b",
            colorB: "#55ab8f",
        });

        tl.current.pause();

        planeInTl.current = gsap.timeline();
        planeInTl.current.pause();
        planeInTl.current.from(airplane.current.position, {
            duration: 3,
            z: 5,
            y: -2,
        });

        planeOutTl.current = gsap.timeline();
        planeOutTl.current.pause();

        planeOutTl.current.to(
            airplane.current.position,
            {
                duration: 10,
                z: -250,
                y: 10,
            },
            0
        );
        planeOutTl.current.to(
            cameraRail.current.position,
            {
                duration: 8,
                y: 12,
            },
            0
        );
        planeOutTl.current.to(airplane.current.position, {
            duration: 1,
            z: -1000,
        });
    }, []);

    useEffect(() => {
        if (play) {
            planeInTl.current.play();
        }
    }, [play]);

    return useMemo(() => (
        <>
            <directionalLight position={[0, 3, 1]} intensity={0.1} />
            <group ref={cameraGroup}>
                <Speed />
                <Background imagePath="/Images/skyscrapper.jpeg" />
                <group ref={cameraRail}>
                    <PerspectiveCamera ref={camera} position={[0, 0, 5]} fov={30} makeDefault />
                </group>
                <group ref={airplane}>
                    <Float floatIntensity={1} speed={1.5} rotationIntensity={0.5}>
                    </Float>
                </group>
            </group>

            {/* Text Sections */}
            {textSections.map((textSection, index) => (
                <TextSection {...textSection} key={index} />
            ))}

            {/* Road Surface with Texture */}
            <group position-y={-2} position-x={1}>
                <mesh>
                    <extrudeGeometry
                        args={[
                            shape,
                            {
                                steps: LINE_NB_POINTS,
                                bevelEnabled: false,
                                extrudePath: curve,
                            },
                        ]}
                    />
                    <meshStandardMaterial
                        map={roadTexture}
                        transparent
                        envMapIntensity={2}
                        onBeforeCompile={fadeOnBeforeCompile}
                    />
                </mesh>

                {/* White Dashed Center Line */}
                <line geometry={dashedLineGeometry} position={[-1, 0.00051, 0]}>
                    <lineDashedMaterial
                        color="white"
                        linewidth={2}
                        dashSize={5}
                        gapSize={5}
                        transparent
                    />
                </line>
            </group>

            {customImages.map((img, index) => (
                <ImagePlane
                    key={index}
                    image={img.image}
                    position={img.position}
                    scale={img.scale}
                    rotation={img.rotation}  // Pass the rotation prop to ImagePlane
                />
            ))}

        </>
    ), []);
};
